import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { ComboSelect } from '../../components/ComboSelect';
import { useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import {
  applyMassFlag, sameRow, toBatchRow, parseMass,
  type MassFlag, type MassPickRow, type TestMethodPair,
} from './icpMassPickModel';
import './SettingsDetailPage.css';
import './module.css';

const BASE = '/api/v1/icpms-mass-picks';

interface ParameterOption { id: string; name: string }

interface Props {
  /** The row to edit (a saved row, or a fresh blank for New). Captured on mount. */
  initialRow: MassPickRow;
  /** Open read-only ('view') or editable ('edit'). View shows Edit/Close; edit shows
   *  the Save trio. In view mode the user clicks Edit to switch to editing. */
  initialMode: 'view' | 'edit';
  parameters: ParameterOption[];
  pairs: TestMethodPair[];
  /** Persist happened — the page reloads and moves to the next view. */
  onSaved: (after: 'new' | 'close') => void | Promise<void>;
  /** Leave the detail (guarded by the page against unsaved edits). */
  onCancel: () => void;
  /** Report the draft's dirty state so the page can guard List-View / navigation. */
  onDirtyChange: (dirty: boolean) => void;
}

function errMsg(e: unknown): string {
  const ax = e as { response?: { data?: { error?: { message?: string } } } };
  return ax?.response?.data?.error?.message ?? 'Save failed.';
}

/**
 * Settings → Utility → e-Data Transfer → ICP-MS Mass Pick — the per-record Detail
 * View (owner request 2026-09-28). ALPACA has no detail for this BO — its ListView is
 * batch-edit only — so this is an AI addition mirroring the shared SettingsDetailPage
 * design: a row opens READ-ONLY (view mode, Edit/Close) and switches to editable
 * (Save trio) on Edit / New. Fields: Element · Matrix + Method (two cascading combos —
 * pick a Matrix, its Methods load) · Parameter (required, unique) · four Select/Mass
 * pairs (mutually exclusive tick). It saves ONE row through the existing atomic
 * `/batch` endpoint, so the create/update rules are enforced server-side exactly as the
 * batch grid's Save did. The List View is read-only; all editing/adding happens here.
 */
export function IcpMassPickDetail({
  initialRow, initialMode, parameters, pairs, onSaved, onCancel, onDirtyChange,
}: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [baseline] = useState(initialRow);
  const [draft, setDraft] = useState<MassPickRow>(initialRow);
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const viewOnly = mode === 'view';

  const recordName = draft.parameterName?.trim() || 'New';
  const patch = (fn: (r: MassPickRow) => MassPickRow) => setDraft((cur) => fn(cur));

  // Matrix + Method are two cascading combos over the (Matrix, Method) pairs: the
  // Matrix list is the distinct matrices; the Method list is the methods for the
  // chosen matrix. testMethodKeys (what the save resolves) is the matching pair's key.
  const matrixOptions = useMemo(
    () => [...new Set(pairs.map((p) => p.matrixName))].sort((a, b) => a.localeCompare(b)),
    [pairs],
  );
  const methodOptions = useMemo(
    () => pairs.filter((p) => p.matrixName === draft.matrix).map((p) => p.methodNumber)
      .sort((a, b) => a.localeCompare(b)),
    [pairs, draft.matrix],
  );
  const keyFor = (matrix: string, method: string) =>
    pairs.find((p) => p.matrixName === matrix && p.methodNumber === method)?.key ?? '';

  const setMatrixMethod = (matrix: string, method: string) =>
    patch((cur) => ({ ...cur, matrix, method, testMethodKeys: keyFor(matrix, method) }));

  const onMatrixChange = (matrix: string) => {
    // Keep the method only if it is still valid for the new matrix; otherwise clear it.
    const keepMethod = pairs.some((p) => p.matrixName === matrix && p.methodNumber === draft.method)
      ? draft.method : '';
    setMatrixMethod(matrix, keepMethod);
  };

  // Keep the page informed of dirtiness so its List-View toggle / navigation can raise
  // the shared unsaved-changes guard (standing rule 15). View mode never edits.
  useEffect(() => { onDirtyChange(!sameRow(baseline, draft)); }, [draft, baseline, onDirtyChange]);

  const save = async (after: 'new' | 'close') => {
    if (!draft.parameter) { setErr('Parameter is required.'); return; }
    setErr('');
    const ok = await confirm({ title: 'Save', message: 'Do you want to Save?' });
    if (!ok) return;
    setSaving(true);
    try {
      // One row through the atomic batch endpoint (same path the grid's Save uses).
      await api.post(`${BASE}/batch`, { rows: [toBatchRow(draft)], deletes: [] });
      notify.success('Saved successfully.');
      onDirtyChange(false);
      await onSaved(after);
    } catch (e) {
      notify.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  // Detail hotkeys (standing rule 14): view → Edit / Close; edit → the Save pair + Cancel.
  useHotkeys(
    viewOnly
      ? [
          { combo: 'alt+e', label: 'Edit', group: 'ICP-MS Mass Pick', order: 1, run: () => setMode('edit') },
          { combo: 'f2', label: 'Edit', group: 'ICP-MS Mass Pick', order: 1, run: () => setMode('edit') },
          { combo: 'esc', label: 'Close', group: 'ICP-MS Mass Pick', order: 2, allowInInput: false, run: () => onCancel() },
        ]
      : [
          { combo: 'mod+enter', label: 'Save & Close', group: 'ICP-MS Mass Pick', order: 1, run: () => void save('close') },
          { combo: 'mod+shift+s', label: 'Save & New', group: 'ICP-MS Mass Pick', order: 2, run: () => void save('new') },
          { combo: 'esc', label: 'Cancel', group: 'ICP-MS Mass Pick', order: 3, allowInInput: false, run: () => onCancel() },
        ],
  );

  const Actions = ({ bottom }: { bottom?: boolean }) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      {viewOnly ? (
        <>
          <button className="sd-abtn primary" onClick={() => setMode('edit')}
            title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
          <button className="sd-abtn cancel" onClick={onCancel}
            title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
        </>
      ) : (
        <>
          <button className="sd-abtn primary" disabled={saving} onClick={() => void save('close')}
            title={`Save & Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save &amp; Close</button>
          <button className="sd-abtn" disabled={saving} onClick={() => void save('new')}
            title={`Save & New (${formatCombo('mod+shift+s')})`}><span className="tb-i">📄</span> Save &amp; New</button>
          <span className="sd-abtn-sep" aria-hidden="true" />
          <button className="sd-abtn cancel" disabled={saving} onClick={onCancel}
            title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
        </>
      )}
    </div>
  );

  const massRow = (n: 1 | 2 | 3 | 4) => {
    const flag = `selectMass${n}` as MassFlag;
    const massKey = `mass${n}` as 'mass1' | 'mass2' | 'mass3' | 'mass4';
    return (
      <div className="sd-row" key={n}>
        <label className="sd-label">Mass {n}</label>
        <div className="icp-massfield">
          <label className="icp-massselect">
            <input
              type="checkbox"
              checked={draft[flag]}
              disabled={viewOnly}
              aria-label={`SelectMass${n}`}
              onChange={(e) => patch((cur) => applyMassFlag(cur, flag, e.target.checked))}
            />
            Select
          </label>
          <input
            type="number"
            step={1}
            className={`sd-input icp-massnum${viewOnly ? ' readonly' : ''}`}
            value={draft[massKey]}
            readOnly={viewOnly}
            aria-label={`Mass${n}`}
            onChange={(e) => {
              const v = parseMass(e.target.value);
              patch((cur) => ({ ...cur, [massKey]: v ?? cur[massKey] }));
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div className="bl-page mlv-host">
        <div className="bl-grid-area">
          <div className="sd-wrap">
            {/* Header — plain title + breadcrumb, matching the shared SettingsDetailPage
                (QC Role) design; no load-time / audit chips in the Detail View. */}
            <div className="sd-head">
              <div>
                <h2>ICP-MS Mass Pick – {recordName}</h2>
                <div className="sd-bc">
                  <a onClick={onCancel}>ICP-MS Mass Pick</a><i className="ti ti-chevron-right" />
                  <span>{recordName}</span>
                </div>
              </div>
            </div>

            <Actions />

            <div className="sd-body">
              <h3 className="sd-section-h">General Information</h3>
              <div className="sd-grid sd-grid--single sd-grid--side sd-grid--square">
                {/* Field order (owner request 2026-09-28): Element · Matrix · Method ·
                    Parameter, then the four Mass pairs. */}
                <div className="sd-row">
                  <label className="sd-label">Element</label>
                  <input
                    className={`sd-input${viewOnly ? ' readonly' : ''}`}
                    value={draft.element}
                    readOnly={viewOnly}
                    onChange={(e) => patch((cur) => ({ ...cur, element: e.target.value }))}
                  />
                </div>

                {/* Matrix → Method cascade: choosing a Matrix loads its Methods. */}
                <div className="sd-row">
                  <label className="sd-label">Matrix</label>
                  <ComboSelect
                    zebra
                    className={`sd-combo${viewOnly ? ' readonly' : ''}`}
                    value={matrixOptions.includes(draft.matrix) ? draft.matrix : ''}
                    options={matrixOptions}
                    placeholder="— Select —"
                    disabled={viewOnly}
                    onChange={(v) => onMatrixChange(v)}
                  />
                </div>

                <div className="sd-row">
                  <label className="sd-label">Method</label>
                  <ComboSelect
                    zebra
                    className={`sd-combo${viewOnly ? ' readonly' : ''}`}
                    value={methodOptions.includes(draft.method) ? draft.method : ''}
                    options={methodOptions}
                    placeholder={draft.matrix ? '— Select —' : '— Select a matrix first —'}
                    disabled={viewOnly || !draft.matrix}
                    onChange={(v) => setMatrixMethod(draft.matrix, v)}
                  />
                </div>

                <div className="sd-row">
                  <label className="sd-label">Parameter<span className="req">*</span></label>
                  <ComboSelect
                    zebra
                    className={`sd-combo${err ? ' required-empty' : ''}${viewOnly ? ' readonly' : ''}`}
                    value={draft.parameter}
                    options={parameters.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="— Select —"
                    disabled={viewOnly}
                    onChange={(v) => {
                      const opt = parameters.find((p) => p.id === v);
                      patch((cur) => ({ ...cur, parameter: opt?.id ?? '', parameterName: opt?.name ?? '' }));
                      if (err) setErr('');
                    }}
                  />
                  {err && <span className="sd-field-error">{err}</span>}
                </div>

                {massRow(1)}{massRow(2)}{massRow(3)}{massRow(4)}
              </div>
            </div>

            {/* Bottom action bar sits AFTER sd-body (which is flex:1), so it stays at
                the bottom of the card instead of floating under a short field set. */}
            <Actions bottom />
          </div>
        </div>
      </div>
    </div>
  );
}

export default IcpMassPickDetail;
